import { List, ListGroup, ListItem } from 'framework7-react';
import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import { DynamicAction } from '@shared/types/app';
import * as dayjs from 'dayjs';
import { groupBy } from 'lodash';
import Markdown from 'markdown-to-jsx';

import {
  CurrentEntertainer,
  useCurrentEntertainerQuery,
  useFetchCurrentEntertainer,
} from '@queries/EntertainerQueries';
import { FeedEvent, useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { Series } from '@queries/TagQueries';

import { useAddEntertainerToEventMutation } from '@mutations/EntertainerMutations';

import { useEventMetaFilter } from '@hooks/EventHooks/useEventMetaFilter';
import { useAnalytics } from '@hooks/useAnalytics';
import { useCityById } from '@hooks/useCurrentUserLocation';

import { useNavigate } from '@router/index';

import Button from '@components/buttons/Button';
import { DynamicActionButton } from '@components/buttons/DynamicActionButton';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { InlineDatePicker } from '@components/layout/InlineDatePicker/InlineDatePicker';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { useQueueEntertainerInstagramMutation } from '@screens/LogInScreen/EntertainerLoginContent';

import { OpenStageSection } from './OpenStageSection';
import { ShowListItem } from './ShowListItem';
import { ShowsList } from './ShowsList';

export const useJoinLineup = ({
  cityId,
  primaryEntertainerType,
  referredBy,
}: {
  cityId?: string;
  primaryEntertainerType: string;
  referredBy?: string;
}) => {
  const { track } = useAnalytics();
  const { openConfirmDialog } = useDialog();
  const { createAuthGateHandler } = useAuthGate();
  const { entertainer } = useCurrentEntertainerQuery();
  const fetchCurrentEntertainer = useFetchCurrentEntertainer();
  const { addEntertainerToEventAsync } = useAddEntertainerToEventMutation();

  const handleJoinLineup = (
    eventId: string,
    cb: (createdEntertainer: Pick<CurrentEntertainer, '_id' | 'title'>) => void,
  ) => {
    track('Join Lineup', { eventId });

    openConfirmDialog({
      title: "You're in the cast?",
      text: 'This is only for entertainers who are already part of this cast and want their name to show up in the lineup. Are you sure you want to continue?',
      onConfirm: () => {
        createAuthGateHandler(
          async () => {
            let addedEntertainer = entertainer;

            if (!addedEntertainer?._id) {
              const { entertainer: refetchedEntertainer } = await fetchCurrentEntertainer();
              addedEntertainer = refetchedEntertainer;
            }

            await addEntertainerToEventAsync({
              entertainerId: addedEntertainer?._id,
              eventId,
              referredBy,
            });
            await cb(addedEntertainer);
          },
          {
            entertainerCallback: () => {},
            cityId,
            entertainerType: primaryEntertainerType,
          },
        );
      },
      buttonOk: 'Continue',
    });
  };

  return { handleJoinLineup };
};

export const ShowsTabContent: FC<{
  onAddEvent: () => void;
  hideOpenStages?: boolean;
  titleOverride?: {
    collapseTopMargin?: boolean;
    text?: string;
  };
  filter?: string;
  cityId?: string;
  tagIds: string[];
  primaryEntertainerType?: 'comedian' | 'dj' | 'dragPerformer';
  isSuggestionModeDefault?: boolean;
  back: string;
  hideEntertainerMenuButton?: boolean;
  addShowText?: string;
  listItemAction?: '';
}> = ({
  onAddEvent,
  hideOpenStages,
  titleOverride,
  filter: initialFilter = '',
  cityId,
  tagIds = [],
  back: initialBack,
  isSuggestionModeDefault,
  hideEntertainerMenuButton,
  addShowText,
  primaryEntertainerType,
}) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { city: currentCity } = useCityById(cityId);
  const [selectedDates, setSelectedDates] = useState<Record<string, boolean>>({});
  const [isSuggestionMode, setIsSuggestionMode] = useState(isSuggestionModeDefault);
  const {
    eventsAreReady,
    events = [],
    eventsMeta,
  } = useUnauthorizedEventsQuery(
    {
      tagIds,
      startDate: dayjs().format('YYYY-MM-DD'),
      rsvpData: 'ignore',
    },
    Boolean(tagIds.length),
  );
  const { filterValue, updateFilterValue, currentFilter, filteredEvents } = useEventMetaFilter(
    events,
    eventsMeta?.filters ?? [],
    initialFilter,
  );

  const back = useMemo(() => {
    const [rootUrl, queryString] = initialBack.split('?');
    const queryParams = new URLSearchParams(queryString);

    if (filterValue) {
      queryParams.set('filter', filterValue);
    } else {
      queryParams.delete('filter');
    }

    if (isSuggestionMode) {
      queryParams.set('isSuggestionModeDefault', 'true');
    } else {
      queryParams.delete('isSuggestionModeDefault');
    }

    return `${rootUrl}?${queryParams.toString()}`;
  }, [initialBack, filterValue, isSuggestionMode]);

  const eventsGroupedByStartDate = useMemo<Record<string, FeedEvent[]>>(() => {
    return groupBy(events, 'startDate');
  }, [events]);

  const predateFilteredEvents = useMemo(
    () => (currentFilter && !isSuggestionMode ? filteredEvents : events),
    [currentFilter, filteredEvents, isSuggestionMode],
  );

  const filteredEventsGroupsByStartDate = useMemo(() => {
    if (!predateFilteredEvents.length) {
      return {};
    }

    const predateFilteredEventsGroupedByStartDate: Record<string, FeedEvent[]> = groupBy(
      predateFilteredEvents,
      'startDate',
    );
    const hasSelectedDates = Object.values(selectedDates).some((isSelected) => isSelected);

    if (hasSelectedDates) {
      return Object.fromEntries(
        Object.entries(predateFilteredEventsGroupedByStartDate).filter(
          ([date]) => selectedDates[date],
        ),
      );
    }

    return predateFilteredEventsGroupedByStartDate;
  }, [selectedDates, predateFilteredEvents]);

  // if (userIsLoggedIn && entertainer?._id) {
  //   onCanAddEvent();
  // } else {
  //   track('Open Add Event Confirm Dialog');
  //   openConfirmDialog({
  //     title: 'Add An Event',
  //     text: 'We currently only allow entertainers to add events. Would you like to continue as an entertainer?',
  //     buttonOk: 'Yes',
  //     onConfirm: () => {
  //       createAuthGateHandler(async () => {
  //         const { entertainer: currentEntertainer } = await fetchCurrentEntertainer();

  //         if (currentEntertainer._id) {
  //           onCanAddEvent();
  //         } else {
  //           navigate('/entertainer/onboarding', {
  //             state: {
  //               onCompletePath: `/noauth/series/${tagSlug}?addEvent=true`,
  //             },
  //           });
  //         }
  //       });
  //     },
  //   });
  // }

  const handleChangeFilters = (
    updatedFilter: string,
    updatedIsSuggestionMode: boolean | undefined,
  ) => {
    track('Change Show Filters', {
      filter: updatedFilter,
      isSuggestionMode: updatedIsSuggestionMode,
    });
    updateFilterValue(updatedFilter);
    setIsSuggestionMode(updatedIsSuggestionMode);
  };

  const currentSuggestion = useMemo(() => {
    if (isSuggestionMode && currentFilter?.cta?.suggestion && currentFilter?.value !== 'isHyped') {
      const { like, iconUrl } = currentFilter.cta.suggestion;
      return { payload: { like }, iconUrl };
    }
    return undefined;
  }, [isSuggestionMode, currentFilter?.cta?.suggestion]);

  const filterOptions = useMemo(() => {
    return [{ value: '', label: '-- Filter events by --' }, ...(eventsMeta?.filters ?? [])].filter(
      ({ isOnlyInitialFilter, value }) => !isOnlyInitialFilter || initialFilter === value,
    );
  }, [initialFilter, eventsMeta?.filters]);

  return (
    <>
      {!hideOpenStages && <OpenStageSection city={currentCity} back={back} />}
      <TitleToolbar
        text={titleOverride?.text ?? 'Performance Calendar 🎉'}
        size="md"
        className={titleOverride?.collapseTopMargin ? 'mt-0' : ''}
        action={
          <Button
            roundness="rounded"
            size="sm"
            startIcon={<FontAwesomeIcon name="plus" />}
            onClick={onAddEvent}
            style={{ borderRadius: 6 }}
          >
            {addShowText ?? 'Add Show'}
          </Button>
        }
      />
      {eventsAreReady ? (
        <>
          {events.length ? (
            <Card>
              <div className="pb-3">
                <InlineDatePicker
                  events={Object.keys(eventsGroupedByStartDate).map((date) => ({
                    date: dayjs(date).toDate(),
                    color: 'var(--f7-theme-color)',
                  }))}
                  minDate={dayjs().startOf('day').toISOString()}
                  value={selectedDates}
                  onChange={setSelectedDates}
                />
              </div>
            </Card>
          ) : (
            <EmptyState
              title="No upcoming shows"
              text={`No one has added any upcoming shows.${addShowText ? ' Add yours and help folks know what you have coming up!' : ''}`}
            />
          )}
        </>
      ) : (
        <Skeleton height={356} />
      )}

      {eventsMeta?.filters?.length && (
        <div className="d-grid mt-3">
          <div className="d-flex align-items-center">
            <Select
              options={filterOptions}
              value={filterValue}
              onChange={(e) => handleChangeFilters(e.target.value, undefined)}
            />
            {currentFilter && (
              <div>
                <Button
                  variant="flat"
                  color="neutral"
                  startIcon={<FontAwesomeIcon name="times" />}
                  onClick={() => handleChangeFilters('', undefined)}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>

          {currentFilter?.description && (
            <Markdown options={{ forceWrapper: true }}>{currentFilter?.description}</Markdown>
          )}
          {currentFilter?.cta?.text && (
            <>
              {isSuggestionMode ? (
                <DynamicActionButton
                  className={currentFilter?.description ? undefined : 'mt-2'}
                  variant="smooth"
                  color="neutral"
                  roundness="rounded"
                  actionProperties={{
                    action: DynamicAction.SplashNotificationMentionEvent,
                    payload: {
                      cityId: currentCity?._id,
                      filter: filterValue,
                    },
                  }}
                >
                  Suggest Missing Event
                </DynamicActionButton>
              ) : (
                <Button
                  className={currentFilter?.description ? undefined : 'mt-2'}
                  variant="smooth"
                  color="neutral"
                  roundness="rounded"
                  onClick={() => {
                    handleChangeFilters(filterValue, true);
                  }}
                >
                  {currentFilter?.cta?.text}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {eventsAreReady && !predateFilteredEvents.length && filterValue ? (
        <div className="mt-3">
          <EmptyState
            title="No matching events"
            text="No events match this filter"
            button={
              events.length
                ? { text: 'See all events', onClick: () => handleChangeFilters('', undefined) }
                : undefined
            }
          />
        </div>
      ) : (
        <ShowsList
          showsGroupedByStartDate={filteredEventsGroupsByStartDate}
          cityId={cityId}
          back={back}
          currentSuggestion={currentSuggestion}
          hideEntertainerMenuButton={hideEntertainerMenuButton}
          primaryEntertainerType={primaryEntertainerType}
        />
      )}
    </>
  );
};
